import {useRef, useState} from 'react';
import {Camera} from 'lucide-react';
import {type PhotoPick} from '@/lib/photo-capture/image-input';
import {PhotoSourceMenu} from '@/components/photo-capture/PhotoSourceMenu';
import type {RequirementOf} from '../../lib/types';
import {photoBoxStyle, resolveElementSize} from '../../lib/element-size';
import {ReportImage} from '../ReportImage';
import {effectivePositionMode} from '../../lib/position-mode';
import {PhotoStampOverlay} from '../PhotoStampOverlay';
import {SlotMarks} from '../SlotMarks';
import {type ElemProps} from './element-props';
import {buildBaseStyle} from './element-styles';

export function PhotoRenderer({
  item,
  fillMode,
  fillValue,
  onFillPhoto,
  fillReadOnly,
  spanWidth,
}: ElemProps & {item: RequirementOf<'photo'>}) {
  const s = item.style;
  const style = buildBaseStyle(s);
  const cfg = item.config;

  // An absolute photo fills the base box the canvas positions it in; a flow photo is
  // sized by its config. Either way the resolver is the one place that decides.
  const isAbsolute = effectivePositionMode(item) === 'absolute';
  const {width: rawW, height: h} = isAbsolute
    ? ({width: '100%', height: '100%'} as const)
    : resolveElementSize(item);
  // A cell photo the author never sized is sized by its COLUMN, which is what
  // the editor draws and therefore what the author designed. `resolveElementSize`
  // cannot know that -- it sees a requirement, not the box around it -- so the
  // caller that does know says so. See `spanWidth`.
  const w = spanWidth ? '100%' : rawW;
  // A square is a RATIO here, not a computed height -- which is what lets an
  // in-cell image input be square while still stretching to its column, where no
  // stored number could say what the height should be. See `photoBoxStyle`.
  // Never for an absolute photo: it fills the box the canvas positioned it in,
  // and a ratio inside a fixed box would fight the geometry the author dragged.
  const boxSize = photoBoxStyle({
    width: w,
    height: h,
    square: cfg.square === true && !isAbsolute,
  });

  // Controlled capture: the parent uploads the file (crop + stamp happen
  // server-side) and feeds the stored photo URL back via `fillValue`.
  // Read-only fill (completed report) also shows the stored URL, but can't
  // capture a new photo. The editor's preview toggle (no onFillPhoto) shows a
  // local preview only.
  const canPick = fillMode && !fillReadOnly;
  const showFromFill = fillMode && (!!onFillPhoto || !!fillReadOnly);
  const [localSrc, setLocalSrc] = useState<string | null>(null);
  const capturedSrc = showFromFill ? (typeof fillValue === 'string' ? fillValue : null) : localSrc;

  /**
   * The box as drawn, so the crop frame can be the shape the photographer is
   * filling.
   *
   * Measured rather than read off the config, because the config does not know:
   * an in-cell image input with no width stretches to its column, and an image
   * input authored before the square lock existed records no shape at all. What
   * is on screen is the only place the answer lives.
   */
  const boxRef = useRef<HTMLDivElement>(null);
  const measuredAspect = () => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return undefined;
    return rect.width / rect.height;
  };

  const handlePick = (pick: PhotoPick) => {
    if (onFillPhoto) onFillPhoto(pick, measuredAspect());
    else setLocalSrc(URL.createObjectURL(pick.file));
  };

  // The stored photo is already stamped server-side, so suppress the
  // decorative overlay chrome once a fill capture is present.
  const showChrome = !(showFromFill && capturedSrc);

  const box = (
    <div
      ref={boxRef}
      className={`relative overflow-hidden border border-gray-300 rounded bg-gray-50 flex items-center justify-center ${canPick ? 'cursor-pointer' : ''}`}
      style={boxSize}
    >
      {capturedSrc ? (
        // **Taken out of flow, so the photo can never size the box.** `h-full`
        // is a percentage, and a percentage height only resolves against a
        // parent with a definite one -- through the wrappers an absolute slot
        // sits in, that is a chain of `height: 100%` that Chrome resolves and
        // Safari does not always. Where it failed, the image fell back to its
        // NATURAL height, the box grew to fit it, and `object-cover` then
        // cropped against a taller frame: the same report showed square tiles
        // with the location stamp visible in one browser and tall cropped ones
        // without it in the other.
        //
        // `absolute inset-0` makes the box the only thing with a say: it is
        // already `relative`, and an out-of-flow child contributes nothing to
        // its parent's height in any engine. The slot's shape then comes from
        // the slot, which is the one place that knows it.
        <ReportImage
          src={capturedSrc}
          className="absolute inset-0 h-full w-full object-cover"
          alt=""
        />
      ) : (
        !canPick && (
          <span className="text-gray-300 text-xs">{fillReadOnly ? 'No photo' : 'Photo'}</span>
        )
      )}

      {/* A label, not the tap target — the whole box opens the menu, which on a
        phone is the difference between a thumb-sized target and a 10px one. */}
      {canPick && (
        <div
          className={`pointer-events-none absolute inset-x-0 flex items-center justify-center ${
            capturedSrc ? 'bottom-1' : 'bottom-1/2 translate-y-1/2'
          }`}
        >
          <span className="flex items-center gap-1 rounded bg-white/90 px-2 py-1 text-[10px] font-medium text-gray-700 shadow-sm">
            <Camera className="h-3 w-3" strokeWidth={2} />
            {capturedSrc ? 'Change photo' : 'Add photo'}
          </span>
        </div>
      )}

      {/* The burned overlay's own four corners, at the server's proportions.
          A preview only — choosing the logo lives in the toolbar. */}
      {showChrome && <PhotoStampOverlay cfg={cfg} />}

      {/* Marks the slot whose Camera / Gallery menu is open, so on a page of
          look-alike boxes the photographer can see which one the photo is
          going into. Keyed off the trigger button's own `data-state`, which the
          menu sets while open. An overlay rather than the box's border: it has
          to sit above a photo already in the slot, and inset rather than a ring
          outside the box, which a table cell would clip. */}
      {canPick && (
        <div
          aria-hidden="true"
          data-slot="photo-picking"
          className="pointer-events-none absolute inset-0 hidden rounded border-[3px] group-data-[state=open]/photo:block"
          style={{
            borderColor: 'var(--dyna-accent)',
            background: 'color-mix(in srgb, var(--dyna-accent) 12%, transparent)',
          }}
        />
      )}
    </div>
  );

  return (
    <div style={{...style, height: isAbsolute ? '100%' : undefined}} className="relative">
      <SlotMarks required={item.required} config={cfg} />
      {/* One trigger, two sources. The menu's opening tap is also what requests
          iOS compass access — it must not share the tap that opens the camera
          (see PhotoSourceMenu). */}
      {canPick ? (
        <PhotoSourceMenu onPick={handlePick} align="center">
          {/* A real button, not the box with a handler on it: the whole slot is
              the tap target — a thumb-sized one rather than the 10px pill —
              and being a button is what makes it reachable by keyboard and
              announced by a screen reader, which the previous click-handling
              div never was.

              It carries the BOX's own width, and must. A `<button>` sizes to
              fit-content whatever its `display`, so an empty slot inside one
              had no definite width for `boxSize`'s `width: 100%` to resolve
              against — and a square slot states its height as a RATIO with no
              height at all, so the box collapsed to its 1px border and every
              unfilled in-cell image input vanished from the job report. The
              editor never showed it: its cell view draws the words "Image
              input" in flow, and that content is what gave the box a width.
              Stating the width here rather than `w-full` keeps the tap target
              exactly the slot, not the column beside it.

              **And the HEIGHT, for the same reason on the other axis.** Width
              alone fixed the in-cell slot, whose height comes from a ratio or a
              number; an ABSOLUTE slot is sized `100% / 100%` of the box the
              canvas positioned it in, so with the button shrink-wrapping its
              (empty, out-of-flow) content to zero height there was nothing for
              that `100%` to resolve against, and all 50 image inputs on a
              page-designed report were invisible on the fill tab while every
              other element drew. A filled slot survived either way, because the
              `<img>` is in flow. `boxSize.height` is `undefined` for a square
              slot (it states `aspectRatio` instead) and for the editor's
              `minHeight` cell view, so this is inert on both of those paths. */}
          <button
            type="button"
            className="group/photo block"
            style={{width: boxSize.width, height: boxSize.height}}
            aria-label={capturedSrc ? 'Change photo' : 'Add photo'}
          >
            {box}
          </button>
        </PhotoSourceMenu>
      ) : (
        box
      )}
    </div>
  );
}
